import { type MetaFunction } from '@remix-run/cloudflare'
import { Card } from '~/components/ui/card'

export const meta: MetaFunction = () => {
  return [
    { title: 'About | Stellar Blade Mods' },
    { 
      name: 'description', 
      content: 'Join our community-driven Stellar Blade modding hub, created by players for players. Share mods, discover content, and connect with fellow EVE fans.' 
    },
    { name: 'keywords', content: 'Stellar Blade, mods, gaming, community, about, players' },
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/about" }
  ]
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-300/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-48 h-48 bg-purple-300/10 rounded-full blur-2xl animate-bounce delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-32 h-32 bg-indigo-300/10 rounded-full blur-xl animate-ping delay-2000"></div>
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-cyan-300/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-center bg-no-repeat opacity-60"
          style={{ 
            backgroundImage: 'url(/Stellar-Blade.webp)',
            backgroundSize: '80vw auto'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 via-black/20 via-black/60 to-black"></div>
        {/* Animated Particles */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-twinkle"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white/30 rounded-full animate-twinkle delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-white/10 rounded-full animate-twinkle delay-2000"></div>
          <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-white/25 rounded-full animate-twinkle delay-500"></div>
          <div className="absolute top-1/2 left-1/6 w-2 h-2 bg-white/15 rounded-full animate-twinkle delay-1500"></div>
        </div>
        
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              <span className="bg-gradient-to-r from-blue-200 via-purple-200 to-cyan-200 bg-clip-text text-transparent">
                Hey There, Fellow EVE!
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed animate-fade-in-up delay-300">
              I'm just another Stellar Blade player who wanted to build something cool for our community. 
              Let's make this the best place to share and discover mods together!
            </p>
            <div className="mt-8 animate-fade-in-up delay-500">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm">Community Building in Progress</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Personal Story Section */}
          <Card className="p-8 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors duration-300">
                🎮 Why I Built This
              </h2>
              <div className="prose prose-lg text-gray-600">
                <p className="mb-4 transform group-hover:translate-x-2 transition-transform duration-300">
                  Hey! I'm a huge Stellar Blade fan who got tired of searching through random forums 
                  and Discord servers just to find decent mods. You know the struggle - finding a 
                  cool mod screenshot on Twitter, but no download link anywhere, or stumbling upon 
                  amazing mods buried in some random Reddit thread.
                </p>
                <p className="mb-4 transform group-hover:translate-x-2 transition-transform duration-300 delay-100">
                  After spending way too many hours hunting for mods (when I could've been playing!), 
                  I decided to build what I wish existed from day one. This isn't some corporate website - 
                  it's a passion project by a player, for players.
                </p>
                <p className="transform group-hover:translate-x-2 transition-transform duration-300 delay-200">
                  My goal? Create a place where we can all share our favorite mods, discover new ones, 
                  and help each other make this amazing game even better. Because honestly, 
                  <span className="text-blue-600 font-semibold">EVE deserves all the love we can give her!</span> 💙
                </p>
              </div>
            </div>
          </Card>

          {/* Community Vision Section */}
          <Card className="p-8 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/10 group">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-purple-600 transition-colors duration-300">
              🤝 Let's Build This Together
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="group/item hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/50 p-4 rounded-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover/item:text-blue-600 transition-colors">
                  👥 By Players, For Players
                </h3>
                <p className="text-gray-600">
                  No corporate BS here. Every feature, every mod, every guide comes from real players 
                  who actually use this stuff. We know what works because we've tested it ourselves.
                </p>
              </div>
              <div className="group/item hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100/50 p-4 rounded-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover/item:text-purple-600 transition-colors">
                  🎨 Share Your Creations
                </h3>
                <p className="text-gray-600">
                  Made something cool? Found an amazing mod? Share it with the community! 
                  Whether it's a simple texture swap or a complete overhaul, every contribution matters.
                </p>
              </div>
              <div className="group/item hover:bg-gradient-to-br hover:from-green-50 hover:to-green-100/50 p-4 rounded-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover/item:text-green-600 transition-colors">
                  🛠️ Help Each Other Out
                </h3>
                <p className="text-gray-600">
                  Stuck with installation? Can't get a mod working? We've all been there! 
                  Our community is here to help troubleshoot and share knowledge.
                </p>
              </div>
              <div className="group/item hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100/50 p-4 rounded-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover/item:text-orange-600 transition-colors">
                  🔒 Keep It Safe & Simple
                </h3>
                <p className="text-gray-600">
                  All mods go through basic safety checks, and we keep installation guides 
                  simple and clear. No technical jargon unless absolutely necessary.
                </p>
              </div>
            </div>
          </Card>

          {/* What We Need Section */}
          <Card className="p-8 bg-gradient-to-br from-gray-50 to-blue-50/50 border-2 border-transparent hover:border-blue-200 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors duration-300">
              🚀 How You Can Help
            </h2>
            <div className="prose prose-lg text-gray-600">
              <p className="mb-4">
                This project is only as good as our community makes it. Here's how you can contribute:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/80 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <span className="mr-2">🎨</span> Content Creators
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center hover:text-blue-600 transition-colors">
                      <span className="mr-2">•</span> Upload your mods and share your work
                    </li>
                    <li className="flex items-center hover:text-blue-600 transition-colors">
                      <span className="mr-2">•</span> Write guides and tutorials
                    </li>
                    <li className="flex items-center hover:text-blue-600 transition-colors">
                      <span className="mr-2">•</span> Create mod showcases and reviews
                    </li>
                    <li className="flex items-center hover:text-blue-600 transition-colors">
                      <span className="mr-2">•</span> Help translate content for international players
                    </li>
                  </ul>
                </div>
                <div className="bg-white/80 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <span className="mr-2">🤝</span> Community Helpers
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center hover:text-purple-600 transition-colors">
                      <span className="mr-2">•</span> Answer questions in our Discord
                    </li>
                    <li className="flex items-center hover:text-purple-600 transition-colors">
                      <span className="mr-2">•</span> Test mods and report compatibility
                    </li>
                    <li className="flex items-center hover:text-purple-600 transition-colors">
                      <span className="mr-2">•</span> Share feedback and feature suggestions
                    </li>
                    <li className="flex items-center hover:text-purple-600 transition-colors">
                      <span className="mr-2">•</span> Help newcomers get started with modding
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Community Section */}
          <Card className="p-8 bg-gradient-to-br from-indigo-900 to-purple-900 text-white relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6 text-center">
                💬 Join the Community
              </h2>
              <div className="text-center">
                <p className="text-blue-100 mb-6 text-lg">
                  Ready to dive in? Join our Discord where most of the magic happens! 
                  We've got dedicated channels for mod sharing, troubleshooting, game discussions, 
                  and just hanging out with fellow Stellar Blade fans.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="https://discord.gg/rE3M6a8u97" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group/button inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-400 hover:to-purple-500 transition-all duration-300 transform hover:scale-110 hover:shadow-2xl shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2 group-hover/button:animate-bounce" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.010c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.197.372.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                    </svg>
                    Join Our Discord
                    <div className="ml-2 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </a>
                  <a 
                    href="/wiki" 
                    className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-sm text-white font-medium rounded-xl hover:bg-white/30 transition-all duration-300 transform hover:scale-110 border border-white/30 hover:border-white/50"
                  >
                    📚 Check Out Our Wiki
                  </a>
                </div>
              </div>
            </div>
          </Card>

          {/* Future Plans Section */}
          <Card className="p-8 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-indigo-600 transition-colors duration-300">
              🌟 What's Coming Next
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🔮 Planned Features</h3>
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg hover:from-blue-100 hover:to-blue-200 transition-all duration-300">
                    <span className="text-2xl mr-3">🔧</span>
                    <span className="text-gray-700">Better mod management tools</span>
                  </div>
                  <div className="flex items-center p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg hover:from-purple-100 hover:to-purple-200 transition-all duration-300">
                    <span className="text-2xl mr-3">⭐</span>
                    <span className="text-gray-700">Rating and review system</span>
                  </div>
                  <div className="flex items-center p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg hover:from-green-100 hover:to-green-200 transition-all duration-300">
                    <span className="text-2xl mr-3">📱</span>
                    <span className="text-gray-700">Mobile app (maybe?)</span>
                  </div>
                  <div className="flex items-center p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg hover:from-orange-100 hover:to-orange-200 transition-all duration-300">
                    <span className="text-2xl mr-3">🌍</span>
                    <span className="text-gray-700">Multi-language support</span>
                  </div>
                  <div className="flex items-center p-3 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg hover:from-cyan-100 hover:to-cyan-200 transition-all duration-300">
                    <span className="text-2xl mr-3">🎮</span>
                    <span className="text-gray-700">Mod collections and playlists</span>
                  </div>
                  <div className="flex items-center p-3 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg hover:from-pink-100 hover:to-pink-200 transition-all duration-300">
                    <span className="text-2xl mr-3">📊</span>
                    <span className="text-gray-700">Mod compatibility tracker</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">💡 Community Ideas</h3>
                <p className="text-gray-600 mb-4">
                  Got ideas for features? I'm all ears! This project grows based on what 
                  the community actually needs and wants.
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-l-4 border-indigo-400 hover:shadow-lg transition-all duration-300">
                    <p className="text-gray-600">
                      <span className="font-medium text-indigo-600">Suggest Features:</span> 
                      <a href="https://discord.gg/rE3M6a8u97" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1 hover:underline">
                        Discord #suggestions
                      </a>
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400 hover:shadow-lg transition-all duration-300">
                    <p className="text-gray-600">
                      <span className="font-medium text-green-600">Report Issues:</span> 
                      <a href="/contact" className="text-blue-600 hover:text-blue-800 ml-1 hover:underline">
                        Contact Form
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Disclaimer */}
          <Card className="p-6 bg-gradient-to-r from-gray-100 to-gray-200 border-l-4 border-blue-400 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="text-sm text-gray-600 text-center relative z-10">
              <strong>Quick note:</strong> This is an independent fan project and not affiliated with 
              Shift Up or Sony. All Stellar Blade assets belong to their respective owners. 
              I'm just a fan who loves this game and wanted to give back to the community! 🎮
            </p>
          </Card>

        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes twinkle {
            0%, 100% { opacity: 0; transform: scale(0.5); }
            50% { opacity: 1; transform: scale(1); }
          }
          
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          .animate-twinkle { animation: twinkle 3s ease-in-out infinite; }
          .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
          .delay-300 { animation-delay: 0.3s; }
          .delay-500 { animation-delay: 0.5s; }
          .delay-1000 { animation-delay: 1s; }
          .delay-1500 { animation-delay: 1.5s; }
          .delay-2000 { animation-delay: 2s; }
        `
      }} />
    </div>
  )
} 